import { useCallback, SyntheticEvent, memo, useMemo } from 'react';
import classnames from 'classnames';
import noop from '@picsart/rc/noop';

import { ICropPatch } from 'types';
import { TRIGGER_PREVIEW_TARGET } from 'configs/constants';

import useStyles from './styles';
import { degreeToRadian, getRanges, needsRatio } from './utils';
import { boxesCount, directions, optionsConfig } from './constants';
import { ICropProps, ICropSides } from './types';
import useChangeHandler from './useChangeHandler';

const getBorderData = ({ cropSizes, objectWidth, objectHeight }: any) => {
  const { y, x, width, height } = cropSizes;
  const actualBorder: ICropSides = {
    top: y,
    left: x,
    right: objectWidth - (width || objectWidth) - x,
    bottom: objectHeight - (height || objectHeight) - y,
  };

  const currentWidth = objectWidth - actualBorder.left - actualBorder.right;
  const currentHeight = objectHeight - actualBorder.top - actualBorder.bottom;

  return { actualBorder, currentWidth, currentHeight };
};

const CropImage = ({ cropSizes, objectWidth, objectHeight, rotation = 0, fullOverlay, onChange = noop, defaultWidth, defaultHeight, itemKey, isOptionsDraw = true }: ICropProps) => {
  const { actualBorder, currentWidth, currentHeight } = getBorderData({ cropSizes, objectWidth, objectHeight });
  const classes = useStyles({
    border: actualBorder,
    rotation,
    fullOverlay,
    objectWidth,
    objectHeight,
    currentWidth,
    currentHeight,
  });

  const rotationRadian = degreeToRadian(rotation < 0 ? rotation + 360 : rotation);

  const onChangeBorder = useCallback(
    (cb: (arg: ICropSides) => ICropSides) => {
      const getPrevBorder = onChange();
      getPrevBorder((prev: ICropPatch) => {
        const currentBorder: ICropSides = cb({
          top: prev.y,
          left: prev.x,
          right: objectWidth - prev.x - prev.width,
          bottom: objectHeight - prev.y - prev.height,
        });
        return {
          width: objectWidth - currentBorder.left - currentBorder.right,
          height: objectHeight - currentBorder.top - currentBorder.bottom,
          x: currentBorder.left,
          y: currentBorder.top,
        };
      });
    },
    [objectWidth, objectHeight, onChange],
  );

  const { rectMoveHandler, badgeMoveHandler, clearLastTouchPosition } = useChangeHandler({ objectWidth, objectHeight, rotationRadian, onChangeBorder });

  const onBadgeChange = useCallback((event: SyntheticEvent<HTMLDivElement>) => {
    const order = directions.findIndex(direction => direction.origin === event.currentTarget.dataset.direction);
    const { origin, scales } = directions[order];
    const ranges = getRanges({ origin, index: order, diagonalAngle: Math.atan(currentWidth / currentHeight), rotationAngle: rotationRadian });
    badgeMoveHandler({ origin, scales, ranges, respectRatio: needsRatio(origin), ratio: (defaultHeight || cropSizes.height || 1) / (defaultWidth || cropSizes.width || 1) });
  }, [badgeMoveHandler, currentHeight, currentWidth, defaultHeight, defaultWidth, cropSizes.height, rotationRadian, cropSizes.width]);

  const onRectChange = useCallback(() => {
    const leftRightRange = objectWidth - currentWidth;
    const topBottomRange = objectHeight - currentHeight;
    rectMoveHandler(leftRightRange, topBottomRange);
  }, [currentHeight, currentWidth, objectHeight, objectWidth, rectMoveHandler]);

  const cropEvents = useMemo(() => (handler: (event: SyntheticEvent<HTMLDivElement>) => void) => {
    if ('ontouchstart' in window) {
      return {
        onTouchStart: handler,
        onTouchEnd: clearLastTouchPosition,
      };
    }
    return {
      onMouseDown: handler,
    };
  }, [clearLastTouchPosition]);

  return (
    <div className={classes.container}>
      <div
        aria-hidden="true"
        {...cropEvents(onRectChange)}
        className={classnames(classes.boxWrap, classes.currentBox)}
      >
        {Array.from({ length: boxesCount }, (_, index) => index).map(i => (
          <div key={`${i}_${itemKey}`} className={classes.box} aria-label={TRIGGER_PREVIEW_TARGET} />
        ))}
        {isOptionsDraw && optionsConfig.map(({ direction, nameOfClass }, index) => (
          <div
            aria-hidden="true"
            key={`${direction}_${itemKey}`}
            data-direction={direction}
            {...cropEvents(onBadgeChange)}
            className={classnames(classes.option, classes[index % 2 === 0 ? 'optionCorner' : 'optionMiddle'], classes[nameOfClass as keyof typeof classes])}
          />
        ))}
      </div>
    </div>
  );
};

export default memo(CropImage);
